'use client';

import * as React from 'react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { useDebouncedValue } from '../hooks/use-debounce';
import { useIsMobile } from '../hooks/use-mobile';
import type { ResolvedAutocompleteProps } from './autocomplete-types';

/**
 * State + behavior for `Autocomplete`. Extracted from `autocomplete.tsx`
 * (conformance, code-size guard). Owns open/highlight/input state, the derived
 * `filteredOptions` / `canCreate` / `displayValue`, keyboard + selection
 * handlers, and the ARIA ids consumed by the desktop and mobile views.
 */
export function useAutocomplete(props: ResolvedAutocompleteProps) {
  const {
    value,
    onChange,
    options,
    label,
    multiple,
    maxSelections,
    creatable,
    onCreate,
    minChars,
    debounceMs,
    onInputChange,
    serverFiltered,
    clearOnSelect,
  } = props;

  // ============================================================================
  // State & Refs
  // ============================================================================
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isCreating, setIsCreating] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const isMobile = useIsMobile();
  const debouncedInputValue = useDebouncedValue(inputValue, debounceMs);

  // Generate unique IDs for ARIA
  const baseId = useId();
  const inputId = `${baseId}-input`;
  const listboxId = `${baseId}-listbox`;
  const labelId = label ? `${baseId}-label` : undefined;
  const statusId = `${baseId}-status`;

  // ============================================================================
  // Computed Values
  // ============================================================================

  // Get selected values as array
  const selectedValues = React.useMemo(() => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }, [value]);

  // Filter options based on input. When `serverFiltered` is set, the options
  // are already the authoritative server result — skip the client-side
  // substring filter so server matches aren't hidden mid-keystroke.
  const filteredOptions = React.useMemo(() => {
    if (serverFiltered || !inputValue || inputValue.length < minChars) {
      return options;
    }
    const searchTerm = inputValue.toLowerCase();
    return options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(searchTerm) ||
        opt.description?.toLowerCase().includes(searchTerm),
    );
  }, [options, inputValue, minChars, serverFiltered]);

  // Check if we can create a new option
  const canCreate = React.useMemo(() => {
    if (!creatable || !inputValue.trim()) return false;
    const normalizedInput = inputValue.trim().toLowerCase();
    return !options.some((opt) => opt.label.toLowerCase() === normalizedInput);
  }, [creatable, inputValue, options]);

  // Get the display value for the input. In `multiple` mode the input is the
  // type-to-filter field and the selected set is surfaced separately (e.g. a
  // badge strip), so we never echo the joined selection back into it — that
  // would overflow the single-line field and duplicate the external display.
  const displayValue = React.useMemo(() => {
    if (inputValue) return inputValue;
    if (multiple) return '';
    if (!value) return '';
    if (typeof value === 'string') {
      return options.find((o) => o.value === value)?.label || '';
    }
    return '';
  }, [value, inputValue, multiple, options]);

  // ============================================================================
  // Effects
  // ============================================================================

  // Call onInputChange with debounced value
  useEffect(() => {
    if (onInputChange && debouncedInputValue) {
      onInputChange(debouncedInputValue);
    }
  }, [debouncedInputValue, onInputChange]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    }

    if (isOpen && !isMobile) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, isMobile]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const optionEls = listRef.current.querySelectorAll('[role="option"]');
      const highlighted = optionEls[highlightedIndex];
      if (highlighted) {
        highlighted.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);
      setHighlightedIndex(-1);

      if (!isOpen && newValue.length >= minChars) {
        setIsOpen(true);
      }
    },
    [isOpen, minChars],
  );

  // At the selection cap (multiple + maxSelections): adds are ignored, removals
  // still allowed. Views use this to render the "select up to N" hint.
  const atLimit = React.useMemo(
    () =>
      !!multiple && !!maxSelections && selectedValues.length >= maxSelections,
    [multiple, maxSelections, selectedValues.length],
  );

  const handleSelect = useCallback(
    async (optionValue: string) => {
      if (multiple) {
        const isSelected = selectedValues.includes(optionValue);
        // Enforce the cap: ignore a NEW add once at the limit.
        if (
          !isSelected &&
          maxSelections &&
          selectedValues.length >= maxSelections
        ) {
          return;
        }
        const newValue = isSelected
          ? selectedValues.filter((v) => v !== optionValue)
          : [...selectedValues, optionValue];
        onChange(newValue.length > 0 ? newValue : null);
      } else {
        onChange(optionValue);
        setIsOpen(false);
      }

      if (clearOnSelect) {
        setInputValue('');
      } else if (!multiple) {
        const selectedOption = options.find((o) => o.value === optionValue);
        setInputValue(selectedOption?.label || '');
      }

      setHighlightedIndex(-1);
    },
    [multiple, maxSelections, selectedValues, onChange, clearOnSelect, options],
  );

  const handleCreate = useCallback(async () => {
    if (!creatable || !onCreate || !inputValue.trim()) return;

    setIsCreating(true);
    try {
      const newOption = await onCreate(inputValue.trim());
      await handleSelect(newOption.value);
    } finally {
      setIsCreating(false);
    }
  }, [creatable, onCreate, inputValue, handleSelect]);

  const handleClear = useCallback(() => {
    onChange(null);
    setInputValue('');
    inputRef.current?.focus();
  }, [onChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const optionsCount = filteredOptions.length + (canCreate ? 1 : 0);

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
          } else {
            setHighlightedIndex((prev) =>
              prev < optionsCount - 1 ? prev + 1 : 0,
            );
          }
          break;

        case 'ArrowUp':
          e.preventDefault();
          if (isOpen) {
            setHighlightedIndex((prev) =>
              prev > 0 ? prev - 1 : optionsCount - 1,
            );
          }
          break;

        case 'Enter':
          e.preventDefault();
          if (isOpen && highlightedIndex >= 0) {
            if (highlightedIndex < filteredOptions.length) {
              const option = filteredOptions[highlightedIndex];
              if (option && !option.disabled) {
                handleSelect(option.value);
              }
            } else if (canCreate) {
              handleCreate();
            }
          } else if (!isOpen) {
            setIsOpen(true);
          }
          break;

        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          setHighlightedIndex(-1);
          inputRef.current?.blur();
          break;

        case 'Tab':
          setIsOpen(false);
          setHighlightedIndex(-1);
          break;

        case 'Backspace':
          // Chip-field affordance: backspace on an empty input removes the
          // last selected chip (standard tag-input behavior).
          if (multiple && !inputValue && selectedValues.length > 0) {
            handleSelect(selectedValues[selectedValues.length - 1]!);
          }
          break;
      }
    },
    [
      isOpen,
      highlightedIndex,
      filteredOptions,
      canCreate,
      handleSelect,
      handleCreate,
      multiple,
      inputValue,
      selectedValues,
    ],
  );

  const handleInputFocus = useCallback(() => {
    if (inputValue.length >= minChars || options.length > 0) {
      setIsOpen(true);
    }
  }, [inputValue.length, minChars, options.length]);

  return {
    isOpen,
    setIsOpen,
    inputValue,
    highlightedIndex,
    isCreating,
    inputRef,
    listRef,
    containerRef,
    isMobile,
    baseId,
    inputId,
    listboxId,
    labelId,
    statusId,
    selectedValues,
    atLimit,
    filteredOptions,
    canCreate,
    displayValue,
    handleInputChange,
    handleSelect,
    handleCreate,
    handleClear,
    handleKeyDown,
    handleInputFocus,
  };
}

export type AutocompleteState = ReturnType<typeof useAutocomplete>;

export interface AutocompleteViewProps {
  props: ResolvedAutocompleteProps;
  state: AutocompleteState;
}
